import { formatDateShort } from "./dateFormatter";

const startOfDay = (d?: string | Date) => {
  if (!d) return null;
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const isAfterEndDate = (endDate?: string): boolean => {
  if (!endDate) return false;
  const today = startOfDay(new Date());
  const contestEnd = startOfDay(endDate);
  if (!today || !contestEnd) return false;
  return today.getTime() > contestEnd.getTime();
};

export const isBeforeRevealDate = (productRevealDate?: string): boolean => {
  if (!productRevealDate) return false;
  const today = startOfDay(new Date());
  const reveal = startOfDay(productRevealDate);
  if (!today || !reveal) return false;
  return today.getTime() < reveal.getTime();
};

export const isParticipatedAndEntriesClosed = (
  contestState?: string,
  endDate?: string,
  isUserParticipated?: boolean
): boolean => {
  if (!isUserParticipated || !endDate) return false;
  return isAfterEndDate(endDate);
};

export const getContestUI = (
  campaignData: any,
  upcomingCampaignData: any,
  contestState: string,
  wishlistCount: number,
  isLoggedIn: boolean,
  campaignOptInData?: boolean,
  isUserParticipated?: boolean
): any => {
  const participated = isUserParticipated;
  const afterEnd = isAfterEndDate(campaignData?.endDate);
  const beforeReveal = isBeforeRevealDate(campaignData?.metadata?.productRevealDate);
  const partialWishlist = wishlistCount < 5 && campaignOptInData;
  const wishlistEmpty = wishlistCount === 0;

  if (!participated && afterEnd) {
    return {
      title: "Entries Closed",
      subtitle: upcomingCampaignData?.startDate
        ? `You just missed this one.\nNext Car Drop: ${formatDateShort(upcomingCampaignData?.startDate)}`
        : "You just missed this one.",
      buttonText: "We'll notify you on ",
      showSteps: true,
      showDateReveal: false,
      showFullDateDetails: false,
      showDisclaimer: false,
      buttonDisabled: true,
      useReducedBottomSection: true,
      useUpcomingDates: true,
      showButton: false,
    };
  }

  if (participated && afterEnd) {
    return {
      title: "Entries Closed",
      subtitle: "Buckle up!\nNext stop: Winner reveal.",
      buttonText: "We'll notify you on ",
      showSteps: false,
      showDateReveal: true,
      showFullDateDetails: false,
      showDisclaimer: false,
      buttonDisabled: true,
      useReducedBottomSection: true,
      useUpcomingDates: false,
      showButton: false,
    };
  }

  if (participated && beforeReveal) {
    return {
      title: "Exclusive for Car Policyholders",
      subtitle: "You're good to go.\nNext stop: the reveal!",
      buttonText: "We'll notify you on ",
      showSteps: false,
      showDateReveal: true,
      showFullDateDetails: true,
      showDisclaimer: false,
      buttonDisabled: true,
      useReducedBottomSection: true,
      useUpcomingDates: false,
      showButton: false,
    };
  }

  if (participated && !beforeReveal) {
    return {
      title: "Exclusive for Car Policyholders",
      subtitle: "Buckle up!\nNext stop: Winner reveal.",
      buttonText: "We'll notify you on ",
      showSteps: false,
      showDateReveal: true,
      showFullDateDetails: false,
      showDisclaimer: false,
      buttonDisabled: true,
      useReducedBottomSection: true,
      useUpcomingDates: false,
      showButton: false,
    };
  }

  if (beforeReveal && !participated) {
    if (partialWishlist) {
      return {
        title: "Exclusive for Car Policyholders",
        subtitle: "Every car you wishlist brings you closer to a win.",
        buttonText: isLoggedIn ? "Finish wishlisting cars" : "Check eligibility",
        showSteps: true,
        showDateReveal: true,
        showFullDateDetails: true,
        showDisclaimer: false,
        buttonDisabled: false,
        useReducedBottomSection: false,
        useUpcomingDates: false,
        showButton: true,
      };
    }

    if (wishlistEmpty) {
      return {
        title: "Exclusive for Car Policyholders",
        subtitle: "Win a new car for FREE!",
        additionalSubTitle: "Just wishlist 5 cars to enter",
        buttonText: isLoggedIn ? "Start wishlisting cars" : "Check eligibility",
        showSteps: true,
        showDateReveal: true,
        showFullDateDetails: true,
        showDisclaimer: true,
        buttonDisabled: false,
        useReducedBottomSection: false,
        useUpcomingDates: false,
        showButton: true,
      };
    }

    return {
      title: "Exclusive for Car Policyholders",
      subtitle: "Win a new car for FREE!",
      additionalSubTitle: "Just wishlist 5 cars to enter",
      buttonText: isLoggedIn ? "Start wishlisting cars" : "Check eligibility",
      showSteps: true,
      showDateReveal: true,
      showFullDateDetails: true,
      showDisclaimer: true,
      buttonDisabled: false,
      useReducedBottomSection: false,
      useUpcomingDates: false,
      showButton: true,
    };
  }

  if (!beforeReveal && !participated) {
    if (partialWishlist) {
      return {
        title: "Exclusive for Car Policyholders",
        subtitle: "Wishlist is pending.\nSo is your chance to win.",
        buttonText: isLoggedIn ? "Complete your wishlist" : "Check eligibility",
        showSteps: true,
        showDateReveal: true,
        showFullDateDetails: false,
        showDisclaimer: false,
        buttonDisabled: false,
        useReducedBottomSection: false,
        useUpcomingDates: false,
        showButton: true,
      };
    }
    
    if (wishlistEmpty) {
      return {
        title: "Exclusive for Car Policyholders",
        subtitle: "Your wishlist could win you this stunning car!",
        buttonText: isLoggedIn ? "Start wishlisting cars" : "Check eligibility",
        showSteps: true,
        showDateReveal: true,
        showFullDateDetails: false,
        showDisclaimer: true,
        buttonDisabled: false,
        useReducedBottomSection: false,
        useUpcomingDates: false,
        showButton: true,
      };
    }

    return {
      title: "Exclusive for Car Policyholders",
      subtitle: "Your wishlist could win you this stunning car!",
      buttonText: isLoggedIn ? "Start wishlisting cars" : "Check eligibility",
      showSteps: true,
      showDateReveal: true,
      showFullDateDetails: false,
      showDisclaimer: true,
      buttonDisabled: false,
      useReducedBottomSection: false,
      useUpcomingDates: false,
      showButton: true,
    };
  }

  return {
    title: "Exclusive for Car Policyholders",
    subtitle: "Your wishlist could be the key to this car!",
    buttonText: isLoggedIn ? "Start wishlisting cars" : "Check eligibility",
    showSteps: true,
    showDateReveal: true,
    showFullDateDetails: false,
    showDisclaimer: true,
    buttonDisabled: false,
    useReducedBottomSection: false,
    useUpcomingDates: false,
    showButton: true,
  };
};